import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { GlassWaterIcon, PartyPopperIcon, TheaterIcon, TentIcon, UsersIcon, SparklesIcon, CheckCircleIcon } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
const fadeInUp = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true
  },
  transition: {
    duration: 0.6
  }
};
const eventTypes = [{
  name: 'Bars & kleine Venues',
  icon: GlassWaterIcon,
  description: 'Intime Atmosphäre für Live-Musik'
}, {
  name: 'Privatpartys',
  icon: PartyPopperIcon,
  description: 'Geburtstage, Hochzeiten und private Feiern'
}, {
  name: 'Kulturveranstaltungen',
  icon: TheaterIcon,
  description: 'Kulturelle Events und Konzerte'
}, {
  name: 'Lokale Festivals',
  icon: TentIcon,
  description: 'Stadt- und Musikfestivals'
}, {
  name: 'Gemeinschaftsevents',
  icon: UsersIcon,
  description: 'Vereinsfeste und Community-Events'
}, {
  name: 'Besondere Anlässe',
  icon: SparklesIcon,
  description: 'Firmenevents und besondere Veranstaltungen'
}];
export function Booking() {
  useSEO({
    title: 'Tuesday buchen – Liveband für Hochzeit & Event im Allgäu',
    description: 'Bucht Tuesday als Liveband aus Kempten für Hochzeit, Firmenevent, Geburtstag, Bar, Festival oder Privatfeier im Allgäu und in ganz Süddeutschland. Anfrage in wenigen Minuten.',
    path: '/buchen'
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    date: '',
    location: '',
    eventType: '',
    setLength: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: ''
      });
    }
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) newErrors.name = 'Bitte gib deinen Namen an';
    if (!formData.email.trim()) newErrors.email = 'Bitte gib deine E-Mail-Adresse an';else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Bitte gib eine gültige E-Mail-Adresse an';
    if (!formData.date) newErrors.date = 'Bitte wähle ein Datum';
    if (!formData.location.trim()) newErrors.location = 'Bitte gib den Veranstaltungsort an';
    if (!formData.eventType) newErrors.eventType = 'Bitte wähle einen Veranstaltungstyp';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          access_key: '1378dc51-eb8c-468c-989e-3f1e1a63d1c9',
          subject: 'Neue Buchungsanfrage von der Tuesday Website',
          from_name: 'Tuesday Website',
          name: formData.name,
          email: formData.email,
          date: formData.date,
          location: formData.location,
          eventType: formData.eventType,
          setLength: formData.setLength,
          message: formData.message
        })
      });
      const data = await res.json();
      if (data.success) {
        setSubmitted(true);
        setFormData({
          name: '',
          email: '',
          date: '',
          location: '',
          eventType: '',
          setLength: '',
          message: ''
        });
      } else {
        setSubmitError(data.message || 'Beim Senden ist ein Fehler aufgetreten. Bitte versuche es später erneut.');
      }
    } catch {
      setSubmitError('Beim Senden ist ein Fehler aufgetreten. Bitte versuche es später erneut.');
    } finally {
      setSubmitting(false);
    }
  };
  if (submitted) {
    return <div className="w-full min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8" data-id="element-77">
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.5
      }} className="max-w-md w-full text-center" data-id="element-78">
          <CheckCircleIcon size={64} className="text-accent mx-auto mb-6" data-id="element-79" />
          <h2 className="font-display text-3xl font-bold text-primary mb-4" data-id="element-80">
            Anfrage gesendet!
          </h2>
          <p className="text-muted mb-6" data-id="element-81">
            Vielen Dank für deine Anfrage. Wir melden uns so schnell wie möglich
            bei dir.
          </p>
          <button onClick={() => setSubmitted(false)} className="px-6 py-2 border-2 border-border-base text-primary font-medium rounded hover:border-accent hover:text-accent transition-colors" data-id="element-82">
            Weitere Anfrage senden
          </button>
        </motion.div>
      </div>;
  }
  return <div className="w-full min-h-screen py-16 px-4 sm:px-6 lg:px-8" data-id="element-83">
      <div className="max-w-7xl mx-auto" data-id="element-84">
        <motion.div {...fadeInUp} className="text-center mb-12" data-id="element-85">
          <h1 className="font-display text-5xl sm:text-6xl font-bold text-primary mb-6" data-id="element-86">
            Tuesday buchen
          </h1>
          <p className="text-muted text-lg max-w-3xl mx-auto" data-id="element-87">
            Tuesday ist als Liveband aus Kempten im Allgäu buchbar für flexible
            Live-Sets aus Covers und eigenen Produktionen, anpassbar nach Länge
            und Atmosphäre. Erzählt uns von eurem Event in Kempten, dem Allgäu
            oder Süddeutschland und wir erstellen ein passendes Angebot.
          </p>
        </motion.div>

        {/* Event Types */}
        <section className="mb-16" data-id="element-88">
          <motion.h2 {...fadeInUp} className="font-display text-3xl font-bold text-primary text-center mb-8" data-id="element-89">
            Veranstaltungstypen
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-90">
            {eventTypes.map((type, index) => <motion.div key={type.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="bg-surface border border-border-base rounded-lg p-6 hover:border-accent transition-colors" data-id="element-91">
                <type.icon size={32} className="text-accent mb-4" data-id="element-92" />
                <h3 className="font-display text-xl font-semibold text-primary mb-2" data-id="element-93">
                  {type.name}
                </h3>
                <p className="text-muted text-sm" data-id="element-94">{type.description}</p>
              </motion.div>)}
          </div>
        </section>

        {/* Booking Form */}
        <motion.div {...fadeInUp} className="max-w-2xl mx-auto" data-id="element-95">
          <div className="bg-surface border border-border-base rounded-lg p-8" data-id="element-96">
            <h2 className="font-display text-2xl font-bold text-primary mb-6" data-id="element-97">
              Buchungsanfrage
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-98">
              <div data-id="element-99">
                <label htmlFor="name" className="block text-sm font-medium text-primary mb-2" data-id="element-100">
                  Name *
                </label>
                <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className="w-full px-4 py-2 bg-input-bg border border-border-base rounded text-primary focus:outline-none focus:border-accent transition-colors" data-id="element-101" />
                {errors.name && <p className="mt-1 text-sm text-error" data-id="element-102">{errors.name}</p>}
              </div>

              <div data-id="element-103">
                <label htmlFor="email" className="block text-sm font-medium text-primary mb-2" data-id="element-104">
                  E-Mail *
                </label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-2 bg-input-bg border border-border-base rounded text-primary focus:outline-none focus:border-accent transition-colors" data-id="element-105" />
                {errors.email && <p className="mt-1 text-sm text-error" data-id="element-106">{errors.email}</p>}
              </div>

              <div data-id="element-107">
                <label htmlFor="date" className="block text-sm font-medium text-primary mb-2" data-id="element-108">
                  Veranstaltungsdatum *
                </label>
                <input type="date" id="date" name="date" value={formData.date} onChange={handleChange} className="w-full px-4 py-2 bg-input-bg border border-border-base rounded text-primary focus:outline-none focus:border-accent transition-colors" data-id="element-109" />
                {errors.date && <p className="mt-1 text-sm text-error" data-id="element-110">{errors.date}</p>}
              </div>

              <div data-id="element-111">
                <label htmlFor="location" className="block text-sm font-medium text-primary mb-2" data-id="element-112">
                  Veranstaltungsort *
                </label>
                <input type="text" id="location" name="location" value={formData.location} onChange={handleChange} className="w-full px-4 py-2 bg-input-bg border border-border-base rounded text-primary focus:outline-none focus:border-accent transition-colors" data-id="element-113" />
                {errors.location && <p className="mt-1 text-sm text-error" data-id="element-114">{errors.location}</p>}
              </div>

              <div data-id="element-115">
                <label htmlFor="eventType" className="block text-sm font-medium text-primary mb-2" data-id="element-116">
                  Veranstaltungstyp *
                </label>
                <select id="eventType" name="eventType" value={formData.eventType} onChange={handleChange} className="w-full px-4 py-2 bg-input-bg border border-border-base rounded text-primary focus:outline-none focus:border-accent transition-colors" data-id="element-117">
                  <option value="" data-id="element-118">Bitte wählen</option>
                  {eventTypes.map(type => <option key={type.name} value={type.name} data-id="element-119">
                      {type.name}
                    </option>)}
                </select>
                {errors.eventType && <p className="mt-1 text-sm text-error" data-id="element-120">{errors.eventType}</p>}
              </div>

              <div data-id="element-121">
                <label htmlFor="setLength" className="block text-sm font-medium text-primary mb-2" data-id="element-122">
                  Gewünschte Setlänge
                </label>
                <input type="text" id="setLength" name="setLength" value={formData.setLength} onChange={handleChange} placeholder="z.B. 2 x 45 Minuten" className="w-full px-4 py-2 bg-input-bg border border-border-base rounded text-primary focus:outline-none focus:border-accent transition-colors placeholder:text-muted" data-id="element-123" />
              </div>

              <div data-id="element-124">
                <label htmlFor="message" className="block text-sm font-medium text-primary mb-2" data-id="element-125">
                  Nachricht
                </label>
                <textarea id="message" name="message" value={formData.message} onChange={handleChange} rows={4} placeholder="Weitere Details zu eurem Event..." className="w-full px-4 py-2 bg-input-bg border border-border-base rounded text-primary focus:outline-none focus:border-accent transition-colors placeholder:text-muted resize-none" data-id="element-126" />
              </div>

              <button type="submit" disabled={submitting} className="w-full px-6 py-3 bg-accent text-[var(--color-text-inv)] font-medium rounded hover:bg-[var(--color-accent-solid-hover)] transition-colors disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-127">
                {submitting ? 'Wird gesendet…' : 'Anfrage senden'}
              </button>
              {submitError && <p className="mt-3 text-sm text-error text-center" data-id="element-128">
                  {submitError}
                </p>}
            </form>

            <p className="mt-6 text-center text-muted text-sm" data-id="element-129">
              Oder direkt per E-Mail:{' '}
              <a href="mailto:booking@tuesday-band.de" className="text-accent hover:underline" data-id="element-130">
                booking@tuesday-band.de
              </a>
            </p>
          </div>
        </motion.div>
      </div>
    </div>;
}